// Incoming-call ring — full-screen scrim, centered card, pulsing avatar.
// Auto-dismisses when callManager moves the state on (caller cancels,
// timeout, accept/decline). Keyboard/aria-modal so assistive tech reads
// it as a dialog. Card entrance + ring pulse honor prefers-reduced-motion
// via the .call-* classes in globals.css.

import { Phone, PhoneOff, PhoneIncoming } from 'lucide-react'
import Avatar from '@/components/Avatar'
import { useCallManager } from '@/lib/callManager'

export function IncomingCallModal() {
  const { state, acceptCall, declineCall } = useCallManager()
  if (state.kind !== 'incoming-ringing') return null
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Incoming call from ${state.callerName}`}
      style={{
        position: 'fixed', inset: 0,
        background: 'var(--overlay-scrim)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 500, padding: 16,
      }}
    >
      <div
        className="call-card-enter"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 20, padding: 32,
          width: '100%', maxWidth: 380,
          boxShadow: 'var(--shadow-popover-mid)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18,
        }}
      >
        {/* Label */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          color: 'var(--ink-tertiary)',
        }}>
          <PhoneIncoming style={{ width: 12, height: 12 }} />
          <span style={{
            fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>
            Incoming call
          </span>
        </div>

        {/* Avatar w/ pulsing ring */}
        <div style={{ position: 'relative', width: 88, height: 88, marginTop: 4 }}>
          <div
            className="call-pulse-ring"
            aria-hidden="true"
            style={{
              position: 'absolute', inset: -6, borderRadius: '50%',
              border: '2px solid var(--accent)',
              pointerEvents: 'none',
            }}
          />
          <div style={{
            width: 88, height: 88, borderRadius: '50%', overflow: 'hidden',
            position: 'relative',
          }}>
            <Avatar name={state.callerName} size={88} variant="contact" />
          </div>
        </div>

        {/* Caller name + subtitle */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          textAlign: 'center', width: '100%',
        }}>
          <p style={{
            fontSize: 19, fontWeight: 700, color: 'var(--ink-primary)',
            margin: 0, letterSpacing: '-0.01em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            width: '100%',
          }}>
            {state.callerName}
          </p>
          <p style={{
            fontSize: 13, color: 'var(--ink-secondary)', margin: 0,
          }}>
            Direct call
          </p>
        </div>

        {/* Decline / Accept */}
        <div style={{ display: 'flex', gap: 12, width: '100%', marginTop: 6 }}>
          <button
            type="button"
            onClick={declineCall}
            aria-label="Decline call"
            style={{
              flex: 1, height: 48,
              background: 'var(--danger-dim)', color: 'var(--danger)',
              border: '1px solid var(--danger-border)', borderRadius: 12,
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <PhoneOff style={{ width: 16, height: 16 }} /> Decline
          </button>
          <button
            type="button"
            onClick={acceptCall}
            aria-label="Accept call"
            style={{
              flex: 1, height: 48,
              background: 'var(--positive)', color: 'var(--ink-inverse)',
              border: 'none', borderRadius: 12,
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <Phone style={{ width: 16, height: 16 }} /> Accept
          </button>
        </div>
      </div>
    </div>
  )
}
